import type { Claim, Concept, Predicate, ResearchSource } from '../types/researchDemo'

export const syntheticCollectionNotice =
  'Synthetic research collection — all studies, passages and findings are fictional teaching examples.'

export const researchSources: ResearchSource[] = [
  {
    id: 'SRC-001',
    title: 'Indoor heat and task attention',
    discipline: 'Cognitive science',
    design: 'Observational',
    population: '84 adult volunteers in two shared offices',
    method: 'Repeated attention tasks and room-temperature logs over ten days',
    finding: 'Higher room temperature coincided with more attention lapses',
    limit: 'Sleep and workload were not controlled; association does not establish causation',
    passages: [
      {
        id: 'P1',
        text: 'Across the observed sessions, higher indoor temperature was associated with more attention lapses. Participants were not randomly assigned to temperature conditions.',
      },
      {
        id: 'P2',
        text: 'The sample included 84 adult volunteers from two shared offices. Sleep, workload and personal cooling access were not controlled.',
      },
    ],
  },
  {
    id: 'SRC-002',
    title: 'A small cooling intervention trial',
    discipline: 'Environmental design',
    design: 'Randomized pilot',
    population: '40 adults in a simulated office',
    method: 'Random assignment to cooling or comparison conditions for one task session',
    finding: 'The cooling group recorded fewer attention lapses during the session',
    limit: 'Small sample and single session; lasting effects and applicability to other populations remain unknown',
    passages: [
      {
        id: 'P1',
        text: 'Forty adults were randomly assigned to cooling or comparison conditions in a simulated office. The cooling group recorded fewer attention lapses during the single session.',
      },
      {
        id: 'P2',
        text: 'The pilot did not measure sleep, long-term performance or outcomes outside the simulated office. The sample was too small to examine subgroup differences.',
      },
    ],
  },
  {
    id: 'SRC-003',
    title: 'Thermal comfort and overnight recovery',
    discipline: 'Sleep research',
    design: 'Interview study',
    population: '22 shift workers interviewed after a heat event',
    method: 'Semi-structured interviews about sleep and living conditions',
    finding: 'Participants described heat-related sleep disruption and fatigue',
    limit: 'Self-reports do not establish an objective attention effect or causal pathway',
    passages: [
      {
        id: 'P1',
        text: 'Several participants described disrupted sleep during warm nights and fatigue on subsequent workdays. These accounts were collected through interviews.',
      },
      {
        id: 'P2',
        text: 'No objective attention tests or temperature interventions were included. Housing conditions and work schedules differed between participants.',
      },
    ],
  },
  {
    id: 'SRC-004',
    title: 'Cooling access in varied workplaces',
    discipline: 'Public policy',
    design: 'Field observation',
    population: '120 workers across six workplaces',
    method: 'Worksite logs comparing cooling availability and task-error rates',
    finding: 'No consistent association between cooling availability and fewer recorded task errors',
    limit: 'Workload, equipment and reporting practices differed; results are not directly comparable to the laboratory pilot',
    passages: [
      {
        id: 'P1',
        text: 'Across six workplaces, availability of cooling was not consistently associated with fewer recorded task errors. Workload and error-reporting practices varied between sites.',
      },
      {
        id: 'P2',
        text: 'Cooling availability did not establish individual exposure or use. The observations cannot directly confirm or refute a controlled single-session attention result.',
      },
    ],
  },
]

export const concepts: Concept[] = [
  { id: 'CON-HEAT', label: 'Indoor heat', role: 'Exposure', x: 150, y: 80 },
  { id: 'CON-ATTN', label: 'Task attention', role: 'Outcome', x: 610, y: 80 },
  { id: 'CON-SLEEP', label: 'Sleep quality', role: 'Mediator', x: 150, y: 240 },
  { id: 'CON-COOL', label: 'Cooling intervention', role: 'Intervention', x: 610, y: 240 },
  { id: 'CON-ACCESS', label: 'Access to cooling', role: 'Context', x: 150, y: 400 },
  { id: 'CON-WORK', label: 'Workplace conditions', role: 'Context', x: 610, y: 400 },
]

export const predicates: Predicate[] = [
  { id: 'associated-with', label: 'associated with' },
  { id: 'may-influence', label: 'may influence' },
  { id: 'supports-investigation-of', label: 'supports investigation of' },
]

export const comparePrompts = [
  'How does measured exposure differ from cooling availability?',
  'Do attention tasks and workplace error logs measure the same outcome?',
  'Could sleep, workload or reporting practices explain differences?',
]

const seededAt = '2026-09-01T09:00:00.000Z'

export const seedClaims: Claim[] = [
  {
    id: 'REL-001',
    subjectId: 'CON-HEAT',
    predicate: 'associated-with',
    objectId: 'CON-ATTN',
    sourceId: 'SRC-001',
    passageId: 'P1',
    interpretation: 'Indoor heat coincided with more attention lapses in the observed office sessions.',
    limitation: 'Observational association in 84 volunteers; no causal conclusion.',
    status: 'approved',
    version: 1,
    createdAt: seededAt,
    previousVersions: [],
    reviews: [],
  },
  {
    id: 'REL-002',
    subjectId: 'CON-COOL',
    predicate: 'associated-with',
    objectId: 'CON-ATTN',
    sourceId: 'SRC-002',
    passageId: 'P1',
    interpretation:
      'In one simulated-office session, the randomly assigned cooling group recorded fewer attention lapses than the comparison group.',
    limitation: 'Randomized pilot with 40 adults and a single session; lasting effects and other populations are unknown.',
    status: 'proposed',
    version: 1,
    createdAt: seededAt,
    previousVersions: [],
    reviews: [],
  },
  {
    id: 'REL-003',
    subjectId: 'CON-HEAT',
    predicate: 'may-influence',
    objectId: 'CON-SLEEP',
    sourceId: 'SRC-003',
    passageId: 'P1',
    interpretation: 'Shift workers described disrupted sleep during warm nights, followed by fatigue on workdays.',
    limitation: 'Interview accounts from 22 shift workers; self-report only, with no objective sleep or temperature measures.',
    status: 'proposed',
    version: 1,
    createdAt: seededAt,
    previousVersions: [],
    reviews: [],
  },
  {
    id: 'REL-004',
    subjectId: 'CON-ACCESS',
    predicate: 'supports-investigation-of',
    objectId: 'CON-WORK',
    sourceId: 'SRC-004',
    passageId: 'P2',
    interpretation:
      'Cooling availability did not establish individual exposure or use, so workplace conditions should be investigated alongside it.',
    limitation: 'Field logs from six workplaces; availability is not individual use, and sites differed in workload and reporting.',
    status: 'proposed',
    version: 1,
    createdAt: seededAt,
    previousVersions: [],
    reviews: [],
  },
]

export const researchSeededAt = seededAt
